import { create } from 'zustand'

import type { Item } from '@/services/schemas'

type Config = Record<string, Item[]>

type ConfigDataStore = {
  config: Config

  setItem: (hubId: string, itemData: Item) => void
  getItem: (hubId: string, rNumber: string) => Item | undefined
  getAllItems: () => Item[]
  getHubItems: (hubId: string) => Item[]
  initializeHub: (hubId: string) => void
  clearHub: (hubId: string) => void
  clearAllHubs: () => void
}

const useConfigDataStore = create<ConfigDataStore>((set, get) => ({
  config: {},

  // Set or update an item in a specific hub
  setItem: (hubId: string, itemData: Item) => {
    set((state) => {
      const updatedConfig = { ...state.config }

      // Initialize hub array if it doesn't exist
      if (!updatedConfig[hubId]) {
        updatedConfig[hubId] = []
      }

      // Find existing item index by rNumber
      const existingItemIndex = updatedConfig[hubId].findIndex(
        item => item.rNumber === itemData.rNumber,
      )

      if (existingItemIndex !== -1) {
        // Update existing item
        updatedConfig[hubId][existingItemIndex] = { ...itemData, hubId }
      } else {
        // Add new item
        updatedConfig[hubId].push({ ...itemData, hubId })
      }

      // Keep array sorted by rNumber for consistency (optional)
      updatedConfig[hubId].sort((a, b) => {
        if (!a.rNumber || !b.rNumber) return 0
        return a.rNumber.localeCompare(b.rNumber)
      })

      return { config: updatedConfig }
    })
  },

  // Get a specific item from a hub
  getItem: (hubId: string, rNumber: string) => {
    const state = get()
    const hubItems = state.config[hubId]
    if (!hubItems) return undefined

    return hubItems.find(item => item.rNumber === rNumber)
  },

  // Get all items from a specific hub
  getHubItems: (hubId: string) => {
    const state = get()
    return state.config[hubId] || []
  },

  // Get all items from all hubs as a flat array
  getAllItems: () => {
    const state = get()
    return Object.values(state.config).flat()
  },

  // Initialize a hub with empty array (useful for setting up 4 hubs)
  initializeHub: (hubId: string) => {
    set(state => ({
      config: {
        ...state.config,
        [hubId]: state.config[hubId] || [],
      },
    }))
  },

  // Clear all items from a specific hub
  clearHub: (hubId: string) => {
    set(state => ({
      config: {
        ...state.config,
        [hubId]: [],
      },
    }))
  },

  // Clear all hubs
  clearAllHubs: () => {
    set({ config: {} })
  },
}))

export default useConfigDataStore

// Example usage:
/*
// Initialize your 4 hubs
const store = useConfigDataStore.getState()
store.initializeHub('hub1')
store.initializeHub('hub2')
store.initializeHub('hub3')
store.initializeHub('hub4')

// Set an item
store.setItem('hub1', {
  hubId: 'hub1',
  rNumber: '001',
  imageURL: 'https://example.com/image.jpg',
  title: 'Item 1',
  area: 'Area A',
  state: 'available',
  amount: 100,
  position: 'horizontal'
})

// Update the same item (will find by rNumber and update)
store.setItem('hub1', {
  hubId: 'hub1',
  rNumber: '001',
  imageURL: 'https://example.com/new-image.jpg',
  title: 'Updated Item 1',
  area: 'Area B',
  state: 'selected',
  amount: 150,
  position: 'vertical'
})

// Get a specific item
const item = store.getItem('hub1', '001')

// Get all items from a hub
const hubItems = store.getHubItems('hub1')
*/
